import React from "react";
import { Modal, Paper, Button, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import "components/pages/transmissions/modal/process/ProcessModal.css";
import LoadingSpinner from "components/LoadingSpinner";
import { useSelector, useDispatch } from "react-redux";

import {
  changeSelectedRows,
  toggleModal,
  toggleArchiving,
} from "app/store/transmission/slice/transmissionSlice";
import { useArchiveReportsMutation } from "app/store/transmission/api/transmissionTableApi";
import {
  showErrorToast,
  showSuccessToast,
  showToastWithUndo,
} from "utils/toast";

const ArchiveModal = ({ setRowSelection }) => {
  const {
    totalSelectedRows,
    selectedReports,
    isArchiveModal,
    isArchiving,
    processModalState,
  } = useSelector((state) => state.transmission);
  const dispatch = useDispatch();
  const [archiveReports] = useArchiveReportsMutation();

  const undoAction = async (removedReports, processState) => {
    try {
      const newArr = selectedReports?.filter((report) =>
        removedReports.includes(report)
      );

      const result = await archiveReports({
        reports: newArr,
        processModalState,
        isUndo: true,
      }).unwrap();
      const { error, showError } = result;

      if (showError || error) {
        await showToastWithUndo(result?.message, null, true);
      } else {
        showSuccessToast(result?.message);
      }
    } catch (err) {
      showToastWithUndo(err?.data?.message, null, true);
    }
  };

  const onConfirm = async () => {
    try {
      dispatch(toggleArchiving(true));
      const result = await archiveReports({
        reports: selectedReports,
        processModalState,
      }).unwrap();

      const { data, showError, message } = result;

      const undoProcessAction = async () => {
        await undoAction(data?.removedReports, processModalState);
      };

      if (showError) {
        await showToastWithUndo(result?.message, undoProcessAction, true);
      } else {
        if (data.removedReports?.length > 0) {
          await showToastWithUndo(message, undoProcessAction);
          setRowSelection?.({});
        }
      }
    } catch (e) {
      showErrorToast(e?.message);
    } finally {
      dispatch(toggleArchiving(false));
      dispatch(toggleModal({ type: processModalState, value: false }));
      dispatch(changeSelectedRows({}));
    }
  };

  const onClose = () => {
    dispatch(toggleArchiving(false));
    dispatch(toggleModal({ type: processModalState, value: false }));
  };

  return (
    <div>
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={isArchiveModal}
        onClose={onClose}
      >
        <div className="process-modal__container">
          <Paper>
            <div className="process-modal__wrapper">
              <Tooltip title="Close" placement="bottom">
                <div onClick={onClose} className="process-modal__close">
                  <CloseIcon />
                </div>
              </Tooltip>
              <div className="process-modal__text">
                {!isArchiving && processModalState === "archive" && (
                  <div>
                    {totalSelectedRows === 1 && (
                      <div>Are you sure you want to archive this record?</div>
                    )}
                    {totalSelectedRows > 1 && (
                      <div>
                        Are you sure you want to archive these{" "}
                        {totalSelectedRows} records?
                      </div>
                    )}
                  </div>
                )}
                {isArchiving && processModalState === "archive" && (
                  <div>
                    <div className="process-modal__processing">
                      Archiving {totalSelectedRows}{" "}
                      {totalSelectedRows > 1 ? "reports" : "report"}.
                    </div>
                    <div className="process-modal__spinner">
                      <LoadingSpinner color="secondary" />
                    </div>
                  </div>
                )}
              </div>
              <div className="process-modal__buttons">
                {processModalState === "archive" && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={onConfirm}
                    disabled={isArchiving}
                  >
                    Archive
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  disabled={isArchiving}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Paper>
        </div>
      </Modal>
    </div>
  );
};

export default ArchiveModal;
